<template>
  <v-card outlined>
    <v-card-title class="d-flex align-center">
      <v-icon class="mr-2">mdi-notebook-outline</v-icon>
      {{ t('aiAdmin.runs.title') }}
    </v-card-title>
    <v-card-text>
      <div class="d-flex align-center flex-wrap ga-3 mb-2">
        <v-text-field
          v-model="from" type="date" density="compact" hide-details
          :label="t('admConfig.usage.from')" style="max-width: 190px"
        />
        <v-text-field
          v-model="to" type="date" density="compact" hide-details
          :label="t('admConfig.usage.to')" style="max-width: 190px"
        />
        <v-text-field
          v-model="project" density="compact" hide-details clearable
          prepend-inner-icon="mdi-magnify"
          :label="t('aiAdmin.runs.project')" style="max-width: 240px"
          @keyup.enter="reload"
        />
        <v-select
          v-model="outcome" :items="outcomeItems" density="compact" hide-details
          :label="t('aiAdmin.runs.outcome')" style="max-width: 200px"
        />
        <v-btn
          color="primary" variant="tonal" :loading="loading"
          prepend-icon="mdi-refresh" @click="reload"
        >
          {{ t('admConfig.usage.refresh') }}
        </v-btn>
      </div>

      <div class="text-caption text-medium-emphasis mb-3">
        {{ t('aiAdmin.runs.hint') }}
      </div>

      <v-alert v-if="error" type="error" variant="tonal" density="comfortable" class="mb-3">
        {{ error }}
      </v-alert>

      <v-data-table-server
        :headers="headers"
        :items="items"
        :items-length="total"
        :loading="loading"
        :page="page"
        :items-per-page="pageSize"
        :items-per-page-options="[25, 50, 100]"
        density="compact"
        hover
        :loading-text="t('common.loading')"
        :no-data-text="t('aiAdmin.runs.empty')"
        @update:page="onPage"
        @update:items-per-page="onPageSize"
        @click:row="(_, { item }) => openDiary(item)"
      >
        <template #[`item.state`]="{ item }">
          <v-icon size="18" :color="runColor(item)" :title="outcomeLabel(item)">{{ runIcon(item) }}</v-icon>
        </template>
        <template #[`item.startedAt`]="{ item }">{{ fmtDateTime(item.startedAt) }}</template>
        <template #[`item.idProject`]="{ item }">
          <span class="font-weight-medium">{{ item.idProject }}</span>
        </template>
        <template #[`item.verificationId`]="{ item }">
          {{ item.verificationId }}<span v-if="item.stepId" class="text-medium-emphasis"> · {{ item.stepId }}</span>
        </template>
        <template #[`item.userName`]="{ item }">{{ item.userName || '–' }}</template>
        <template #[`item.model`]="{ item }">{{ item.model || '–' }}</template>
        <template #[`item.duration`]="{ item }">{{ duration(item) }}</template>
        <template #[`item.requests`]="{ item }">{{ item.requests ?? '–' }}</template>
        <template #[`item.costUsd`]="{ item }">
          {{ item.costUsd != null ? formatUsdShort(item.costUsd) : '–' }}
        </template>
        <template #[`item.providerCostUsd`]="{ item }">
          {{ item.providerCostUsd != null ? formatUsdShort(item.providerCostUsd) : '–' }}
        </template>
      </v-data-table-server>
    </v-card-text>

    <ProjectAiDiary v-model="showDiary" :projectId="diaryProject" :runId="diaryRun" />
  </v-card>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { useAuthStore } from '../stores/auth.js'
import { apiCall } from '../utils/api.js'
import { formatUsdShort } from '../utils/money.js'
import { fmtDateTime, duration, runIcon, runColor } from '../utils/aiRuns.js'
import ProjectAiDiary from './ProjectAiDiary.vue'

// Elenco trasversale delle esecuzioni IA: GET /api/billing/ai-runs. Il diario di una riga si apre
// con lo stesso dialog della scheda processo, puntato sull'esecuzione scelta.
const { t } = useI18n()
const authStore = useAuthStore()
const isSuperAdmin = computed(() => authStore.user?.role === 'superadmin')

const from = ref('')
const to = ref('')
const project = ref('')
const outcome = ref('')

const items = ref([])
const total = ref(0)
const page = ref(1)
const pageSize = ref(50)
const loading = ref(false)
const error = ref('')

const showDiary = ref(false)
const diaryProject = ref('')
const diaryRun = ref('')

const outcomeItems = computed(() => [
  { title: t('aiAdmin.runs.outcomes.all'), value: '' },
  { title: t('aiAdmin.runs.outcomes.ok'), value: 'ok' },
  { title: t('aiAdmin.runs.outcomes.error'), value: 'error' },
  { title: t('aiAdmin.runs.outcomes.incomplete'), value: 'incomplete' },
])

function outcomeLabel(r) {
  if (r.hasError) return t('aiAdmin.runs.outcomes.error')
  return r.hasResult ? t('aiAdmin.runs.outcomes.ok') : t('aiAdmin.runs.outcomes.incomplete')
}

// Il costo reale del fornitore arriva valorizzato solo al superadmin: per gli altri la colonna
// sarebbe una fila di trattini.
const headers = computed(() => [
  { title: '', key: 'state', sortable: false, width: 36 },
  { title: t('aiAdmin.runs.cols.startedAt'), key: 'startedAt', sortable: false },
  { title: t('aiAdmin.runs.cols.project'), key: 'idProject', sortable: false },
  { title: t('aiAdmin.runs.cols.verification'), key: 'verificationId', sortable: false },
  { title: t('aiAdmin.runs.cols.user'), key: 'userName', sortable: false },
  { title: t('aiAdmin.runs.cols.model'), key: 'model', sortable: false },
  { title: t('aiAdmin.runs.cols.duration'), key: 'duration', sortable: false, align: 'end' },
  { title: t('aiAdmin.runs.cols.requests'), key: 'requests', sortable: false, align: 'end' },
  { title: t('aiAdmin.runs.cols.cost'), key: 'costUsd', sortable: false, align: 'end' },
  ...(isSuperAdmin.value
    ? [{ title: t('admConfig.usage.providerCost'), key: 'providerCostUsd', sortable: false, align: 'end' }]
    : []),
])

async function load() {
  loading.value = true
  error.value = ''
  try {
    const q = new URLSearchParams({ page: page.value, pageSize: pageSize.value })
    if (from.value) q.set('from', from.value)
    if (to.value) q.set('to', to.value)
    if (project.value?.trim()) q.set('project', project.value.trim())
    if (outcome.value) q.set('outcome', outcome.value)
    const res = await apiCall(`/api/billing/ai-runs?${q}`, { method: 'GET' })
    items.value = res?.items || []
    total.value = res?.total || 0
  } catch (e) {
    error.value = e?.message || t('aiAdmin.runs.error')
  } finally {
    loading.value = false
  }
}

function reload() {
  page.value = 1
  load()
}

function onPage(p) {
  if (p === page.value) return
  page.value = p
  load()
}

function onPageSize(n) {
  if (n === pageSize.value) return
  pageSize.value = n
  reload()
}

function openDiary(item) {
  diaryProject.value = item.idProject
  diaryRun.value = item.runId
  showDiary.value = true
}

onMounted(load)
</script>
